import { ChainIdEnum } from "../../enums/chain";
import { resolveUsdcDecimals } from "../tokenDecimals";
import { CHAIN_CONFIGS } from "./configs";
import { toRpcWebSocketUrl } from "./configs/shared";
import { ChainConfig, NativeCurrency, TokenDecimals } from "./types";

/**
 * Get chain ID from environment variable
 * Returns the chain ID specified in REACT_APP_CHAIN_ID
 * Falls back to SEPOLIA_TESTNET if not specified or invalid
 */
export const getChainIdFromEnv = (): ChainIdEnum => {
  const envChainId = process.env.REACT_APP_CHAIN_ID;

  if (!envChainId) {
    // Behavior-preserving: keep the original BASECAMP fallback for a missing env
    // var; only the log message was wrong (it said SEPOLIA). This path is
    // unreachable in real builds (every .env sets REACT_APP_CHAIN_ID).
    console.warn(
      "[ChainConfig] REACT_APP_CHAIN_ID not set, defaulting to BASECAMP_TESTNET"
    );
    return ChainIdEnum.BASECAMP_TESTNET;
  }

  const chainId = parseInt(envChainId, 10);

  if (isNaN(chainId)) {
    console.warn(
      `[ChainConfig] Invalid REACT_APP_CHAIN_ID: ${envChainId}, defaulting to SEPOLIA_TESTNET`
    );
    return ChainIdEnum.SEPOLIA_TESTNET;
  }

  // Check if it's a valid ChainIdEnum
  if (!Object.values(ChainIdEnum).includes(chainId)) {
    console.warn(
      `[ChainConfig] Unknown chain ID: ${chainId}, defaulting to SEPOLIA_TESTNET`
    );
    return ChainIdEnum.SEPOLIA_TESTNET;
  }

  return chainId as ChainIdEnum;
};

/**
 * Get chain configuration by chain ID
 * Returns undefined if chain is not configured
 */
export const getChainConfig = (
  chainId: ChainIdEnum
): ChainConfig | undefined => {
  return CHAIN_CONFIGS.get(chainId);
};

/**
 * Get chain configuration by chain ID (throws if not found)
 */
export const getChainConfigOrThrow = (chainId: ChainIdEnum): ChainConfig => {
  const config = CHAIN_CONFIGS.get(chainId);
  if (!config) {
    throw new Error(`Chain configuration not found for chain ID: ${chainId}`);
  }
  return config;
};

/**
 * Get current chain configuration based on REACT_APP_CHAIN_ID
 * This is the primary way to get the active chain config
 */
export const getCurrentChainConfig = (): ChainConfig => {
  const chainId = getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(chainId);

  if (!config) {
    console.error(
      `[ChainConfig] No configuration found for chain ID: ${chainId}`
    );
    // Return SEPOLIA as fallback
    return CHAIN_CONFIGS.get(ChainIdEnum.SEPOLIA_TESTNET)!;
  }

  return config;
};

/**
 * Get current chain ID from environment
 */
export const getCurrentChainId = (): ChainIdEnum => {
  return getChainIdFromEnv();
};

// =============================================================================
// Convenience getters for commonly accessed config properties
// =============================================================================

/**
 * Get RPC URL for a chain
 */
export const getRpcUrl = (chainId?: ChainIdEnum): string => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.rpcUrls[0] ?? "";
};

/**
 * Get all RPC URLs for a chain
 */
export const getRpcUrls = (chainId?: ChainIdEnum): string[] => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.rpcUrls ?? [];
};

/**
 * Boot check: the active chain must have at least one RPC URL. Chains whose
 * only RPC is keyed (Arc, MegaETH) take it from REACT_APP_RPC_URL and have no
 * committed fallback, so a build without it must fail loudly, not at first read.
 */
export const assertChainRpcConfigured = (
  chainId?: ChainIdEnum,
  config: Pick<ChainConfig, "name" | "rpcUrls"> | undefined = CHAIN_CONFIGS.get(
    chainId ?? getChainIdFromEnv()
  )
): void => {
  if (!config || (chainId ?? getChainIdFromEnv()) === ChainIdEnum.NONE) return;
  if (config.rpcUrls.filter(Boolean).length === 0) {
    throw new Error(
      `[ChainConfig] ${config.name} has no RPC URL. Set REACT_APP_RPC_URL for this build.`
    );
  }
};

/**
 * Get all RPC WebSocket URLs for a chain.
 */
export const getRpcWsUrls = (chainId?: ChainIdEnum): string[] => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  if (!config) return [];

  const derivedRpcWsUrls =
    id === ChainIdEnum.ARC_TESTNET
      ? (config.rpcUrls.map(toRpcWebSocketUrl).filter(Boolean) as string[])
      : [];
  return Array.from(
    new Set([...(config.rpcWsUrls ?? []), ...derivedRpcWsUrls])
  ).filter(Boolean);
};

/**
 * Get explorer URL for a chain
 */
export const getExplorerUrl = (chainId?: ChainIdEnum): string => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.explorerUrl ?? "";
};

/**
 * Get L2 explorer URL for a chain
 */
export const getL2ExplorerUrl = (chainId?: ChainIdEnum): string => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.l2ExplorerUrl ?? config?.explorerUrl ?? "";
};

/**
 * Get native currency symbol for a chain
 */
export const getNativeSymbol = (chainId?: ChainIdEnum): string => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.nativeCurrency.symbol ?? "ETH";
};

/**
 * Get native currency config for a chain
 */
export const getNativeCurrency = (chainId?: ChainIdEnum): NativeCurrency => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return (
    config?.nativeCurrency ?? { name: "Ether", symbol: "ETH", decimals: 18 }
  );
};

/**
 * Get token decimals for a chain
 */
export const getTokenDecimals = (chainId?: ChainIdEnum): TokenDecimals => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  const base: TokenDecimals = config?.tokenDecimals ?? {
    usdc: 6,
    usdt: 6,
    weth: 18,
    wbtc: 8,
    sdai: 6,
  };
  // Durability guard: let the on-chain-verified USDC decimals win over a drifted
  // static config (see resolveUsdcDecimals / commit d51febe). No-op unless the
  // active chain is the one tokenDecimals.generated.json was last verified against
  // and the config actually disagrees, so other chains are unaffected.
  const resolvedUsdc = resolveUsdcDecimals(id, config?.contracts?.erc20?.usdc, base.usdc);
  return resolvedUsdc === base.usdc ? base : { ...base, usdc: resolvedUsdc };
};

/**
 * Get USDC decimals for current chain
 */
export const getUsdcDecimals = (chainId?: ChainIdEnum): number => {
  return getTokenDecimals(chainId).usdc;
};
